<template>
  <component
    v-stack-x:2xs
    :is="type"
    :to="to"
    :href="href"
    :type="buttonType"
    :class="['slo-button', `fill-${fill}`, `size-${size}`, `fill-color-${color}`]"
    v-bind="$attrs"
    v-on="$listeners"
    @click="handleClick($event)"
  >
    <slot />
  </component>
</template>

<script>
export default {
  name: 'SloButton',
  props: {
    modal: {type: String},
    modalProps: {type: Object, default() {return {};}},
    to: {type: Object, default: null},
    href: {type: String, default: null},
    submit: {type: Boolean, default: false},
    fill: {
      type: String,
      default: 'solid',
      validator: value => {
        return value.match(/(solid|outline|clear)/);
      }
    },
    color: {
      type: String,
      default: 'blue',
      validator: value => {
        return value.match(/(blue|white)/);
      }
    },
    size: {
      type: String,
      default: 'default',
      validator: value => {
        return value.match(/(small|default|large)/);
      }
    }
  },
  computed: {
    type() {
      if (this.to !== null) return 'router-link';
      if (this.href !== null) return 'a';
      return 'button';
    },
    buttonType() {
      if (this.submit) return 'submit';
      return 'button';
    }
  },
  methods: {
    handleClick($event) {
      if (typeof this.modal === 'string') {
        this.$modal.show(Object.assign({}, {name: this.modal}, this.modalProps));
      } else if (this.href) {
        if (this.href.slice(0, 1) === '#') {
          const target = document.getElementById(this.href.slice(1));
          if (!target || (target && !target.scrollIntoView)) return;
          $event.preventDefault();
          target.scrollIntoView({behavior: 'smooth'});
        }
      }
    }
  }
};
</script>

<style lang="scss">
@import '../../scss/system';

slo-button {
  @include text('sm', 'strong');
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-decoration: none;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 0;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;

  padding: 12px 18px;
  margin: 0;
  transition: 200ms cubic-bezier(0.23, 1, 0.32, 1);
  transition-property: color, background-color, border-color;

  slo-icon {
    width: 1.6rem;
    height: 1.6rem;
    color: inherit;

    &:first-child {
      margin-right: 8px;
    }

    &:last-child {
      margin-left: 8px;
    }
  }

  &[size='large'] {
    @include text('lg', 'strong');
  }

  &:not([fill]),
  &[fill='solid'] {
    background: $color-blue-400;
    color: $color-white;

    &:hover {
      background: $color-blue-700;
    }

    &:active {
      background: $color-blue-900;
    }

    &:disabled,
    &.disabled {
      background: #80b5da;
      pointer-events: none;
    }
  }

  &[fill='outline'] {
    background: transparent;
    color: $color-blue-400;
    border: 1px solid currentColor;

    &:hover {
      color: $color-blue-700;
    }

    &:active {
      color: $color-blue-900;
      background: rgba($color-blue-400, 0.05);
    }

    &:disabled,
    &.disabled {
      color: #80b5da;
      pointer-events: none;
    }
  }

  &[fill='clear'] {
    background: transparent;
    color: $color-blue-400;
    border: 1px solid transparent;

    &:hover {
      color: $color-blue-700;
    }

    &:active {
      color: $color-blue-900;
      background: rgba($color-blue-400, 0.05);
    }

    &:disabled,
    &.disabled {
      color: #80b5da;
      pointer-events: none;
    }
  }
}
</style>

<style scoped lang="scss">
@import '../../scss/system';

.slo-button {
  @include text('sm', 'strong');
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-decoration: none;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  border: 1px solid $color-blue-400;
  border-radius: 6px;
  background: transparent;
  cursor: pointer;

  padding: 12px 18px;
  margin: 0;
  transition: 200ms cubic-bezier(0.23, 1, 0.32, 1);
  transition-property: color, background-color, border-color;

  .slo-icon {
    width: 1.6rem;
    height: 1.6rem;
    color: inherit;
  }

  &.size-small {
    padding: 8px;
  }

  &.size-large {
    @include text('lg', 'strong');
  }

  &.fill-solid {
    background: $color-blue-400;
    color: $color-white;

    &:hover {
      background: $color-blue-700;
      border-color: $color-blue-700;
    }

    &:active {
      background: $color-blue-900;
      border-color: $color-blue-900;
    }

    &:disabled,
    &.disabled {
      background: #80b5da;
      border-color: #80b5da;
      pointer-events: none;
    }
  }

  &.fill-solid.fill-color-white {
    background: $color-white;
    color: $color-blue-400;
    border-color: $color-white;

    &:hover {
      background: $color-blue-700;
      color: $color-white;
      border-color: $color-blue-700;
    }

    &:active {
      background: $color-blue-900;
      color: $color-white;
      border-color: $color-blue-900;
    }

    &:disabled,
    &.disabled {
      background: #80b5da;
      border-color: #80b5da;
    }
  }

  &.fill-outline {
    background: transparent;
    color: $color-blue-400;
    border-color: currentColor;

    &.fill-color-white {
      color: $color-white;

      &:hover {
        background: $color-white;
        color: $color-blue-400;
      }

      &:active {
        background: $color-white;
        color: $color-blue-400;
      }
    }

    &:hover {
      color: $color-blue-700;
    }

    &:active {
      color: $color-blue-900;
      background: rgba($color-blue-400, 0.05);
    }

    &:disabled,
    &.disabled {
      color: #80b5da;
      pointer-events: none;
    }
  }

  &.fill-clear {
    background: transparent;
    color: $color-blue-400;
    border-color: transparent;

    &:hover {
      color: $color-blue-700;
    }

    &:active {
      color: $color-blue-900;
      background: rgba($color-blue-400, 0.05);
    }

    &:disabled,
    &.disabled {
      color: #80b5da;
      pointer-events: none;
    }
  }

  &:focus-visible {
    outline: 1px solid $color-blue-400 !important;
    outline-offset: 2px !important;
  }
}
</style>
